import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminCarsApi } from "../../http/cars/api";
import { Car, CarNoCarDepency } from "../../http/cars/types";
import { Claim, ClaimStatusType, getClaimStatuses } from "../../http/claims/types";
import { useClaimTypesApi } from "../../http/claimTypes/api";
import { ClaimType } from "../../http/claimTypes/types";
import { useSubsidiariesApi } from "../../http/subsidiaries/api";
import { Subsidiary } from "../../http/subsidiaries/types";
import { useUsersApi } from "../../http/users/api";
import { User } from "../../http/users/types";
import { Currency, getCurrencies } from "../../types";
import Button from "../ui/Button";
import CostInput from "../ui/CostInput";
import DatePicker from "../../ui/DatePicker";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormGrid from "../ui/FormGrid";
import Input from "../ui/Input";
import ModalDialog, { ActionButton } from "../ui/ModalDialog";
import reactSelectStyles from "../ui/ReactSelectUtils";
import Textarea from "../ui/Textarea";

const CLAIM_TYPE_FIELDS = ["id", "name"] as const;
type ClaimTypeField = (typeof CLAIM_TYPE_FIELDS)[number];
type ClaimTypeSearch = Pick<ClaimType, ClaimTypeField>;

const CAR_FIELDS = [
  "id",
  "vehicleIdentificationNumber",
  "currentPlateNumber",
  "lastPlateNumber",
  "carSubsidiary",
  "currentAssignment",
] as const;
type CarField = (typeof CAR_FIELDS)[number];
type CarSearch = Pick<CarNoCarDepency, CarField>;

const USER_FIELDS = ["id", "fullName"] as const;
type UserField = (typeof USER_FIELDS)[number];
type UserSearch = Pick<User, UserField>;

const SUBSIDIARY_FIELDS = ["id", "name"] as const;
type SubsidiaryField = (typeof SUBSIDIARY_FIELDS)[number];
type SubsidiarySearch = Pick<Subsidiary, SubsidiaryField>;

export type ClaimFormBody = {
  car?: Car;
  driver?: User;
  subsidiary?: Subsidiary;
  number?: string;
  status: ClaimStatusType;
  openingDate: string;
  closingDate?: string;
  incidentDate: string;
  type?: ClaimType;
  description?: string;
  damageValueGross?: number | null;
  damageValueNet?: number | null;
  damageValueCurrency?: Currency | null;
  settledAmountGross?: number | null;
  settledAmountNet?: number | null;
  settledAmountCurrency?: Currency | null;
};

type ClaimFormProps = {
  isDisabled?: boolean;
  isLoading?: boolean;
  onSubmit: (formData: ClaimFormBody) => Promise<void>;
  claim?: Claim | null;
  carId?: string | null;
};

const ClaimForm: FC<ClaimFormProps> = ({ claim, onSubmit, isDisabled, isLoading, carId }: ClaimFormProps) => {
  const i18n = useI18n();
  const claimTypesApi = useClaimTypesApi();
  const carsApi = useAdminCarsApi();
  const subsidiariesApi = useSubsidiariesApi();
  const usersApi = useUsersApi();
  const navigate = useNavigate();
  const aborter = useRef(new AbortController());
  const currentCarAborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);
  const currencies = getCurrencies();
  const [defaultCarOptions, setDefaultCarOptions] = useState<CarSearch[]>([]);
  const [defaultUserOptions, setDefaultUserOptions] = useState<UserSearch[]>([]);
  const [defaultSubsidiaryOptions, setDefaultSubsidiaryOptions] = useState<SubsidiarySearch[]>([]);
  const [showDriverMismatchDilog, setShowDriverMismatchDilog] = useState(false);
  const [showSubsidiaryMismatchDialog, setShowSubsidiaryMismatchDialog] = useState(false);
  const isNewClaim = !claim?.id;

  const [params] = useSearchParams();
  const redirectUrl = params.get("redirect_uri");
  // navigate(redirectUrl || `/admin/claims/${response.id}`);

  const statuses = getClaimStatuses(i18n.t);

  const claimTypes = claimTypesApi.useAdminClaimTypes(
    { limit: 100, fields: CLAIM_TYPE_FIELDS },
    aborter.current.signal,
  );

  const cars = carsApi.useCars({ limit: 100, fields: CAR_FIELDS }, aborter.current.signal);

  const currentDriverUser = usersApi.getAdminCurrentDriverUser();
  const currentCar = carsApi.getCar(carId ?? "", currentCarAborter.current.signal);

  const carDriversAborter = useRef(new AbortController());
  const carDrivers = usersApi.useAdminUsers(
    { limit: 100, fields: USER_FIELDS },
    carDriversAborter.current.signal,
    undefined,
    undefined,
    carId ?? undefined,
  );

  useEffect(() => {
    if (claimTypes.error || cars.error || currentCar.error || (carId && carDrivers.error)) {
      dispatch(showErrorNotification(i18n, claimTypes.error ?? cars.error ?? currentCar.error ?? carDrivers.error));
    }
  }, [cars.error, claimTypes.error, currentCar.error, carDrivers.error]);

  const defaultValues: Partial<ClaimFormBody> = {
    car: claim?.car,
    driver: claim?.driver,
    subsidiary: claim?.subsidiary,
    number: claim?.number ?? undefined,
    status: statuses.find((x) => x.value === claim?.status) ?? statuses.find((x) => x.value === "open") ?? statuses[0],
    openingDate: claim?.openingDate,
    closingDate: claim?.closingDate,
    incidentDate: claim?.incidentDate,
    type: claim?.type,
    description: claim?.description,
    damageValueGross: claim?.damageValueGross,
    damageValueNet: claim?.damageValueNet,
    damageValueCurrency: currencies.find((x) => x.value === claim?.damageValueCurrency),
    settledAmountGross: claim?.settledAmountGross,
    settledAmountNet: claim?.settledAmountNet,
    settledAmountCurrency: currencies.find((x) => x.value === claim?.settledAmountCurrency),
  };

  return (
    <Form<ClaimFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
      itemCreationInfo={claim}
      secondaryButton={
        redirectUrl ? (
          <Button buttonProps={{ onClick: () => navigate(redirectUrl) }} type="secondary">
            {i18n.t.common.back}
          </Button>
        ) : undefined
      }
    >
      {({ register, formState: { errors }, control, reset, setValue, watch }) => {
        const selectedCar = watch("car");
        const currentCarSubsidiary =
          selectedCar?.carSubsidiary?.subsidiary ?? currentCar.data?.carSubsidiary?.subsidiary;

        useEffect(() => {
          reset(defaultValues);
          if (!isNewClaim) {
            return;
          }

          const id = carId || claim?.car.id;
          if (!id) {
            return;
          }

          currentDriverUser.trigger(id);
          carId && currentCar.trigger();
        }, [carId, claim, isNewClaim]);

        useEffect(() => {
          if (!isNewClaim) {
            return;
          }

          // if no driver is selected or the selected car currently does not have a driver
          const driver = watch("driver");
          if (!currentDriverUser.data || !driver) {
            if (!driver) {
              // if a car is selectd, we consider the current driver of that car
              const car = watch("car");
              if (car?.currentAssignment.carDriver?.driver.id) {
                setValue("driver", car?.currentAssignment.carDriver?.driver);
                return;
              }

              // otherwise, if no car is selected, that means we have a carId in the url, consider the loaded user
              if (currentDriverUser.data?.id) {
                setValue("driver", currentDriverUser.data);
                return;
              }
            }
            return;
          }

          // if driver is selected but does not match to the current car driver, ask/notify user if switch to other driver
          if (driver?.id !== currentDriverUser.data?.id) {
            setShowDriverMismatchDilog(true);
          }
        }, [currentDriverUser.data?.id, isNewClaim]);

        useEffect(() => {
          if (!isNewClaim) {
            return;
          }

          const subsidiary = watch("subsidiary");
          if (!currentCarSubsidiary?.id || !subsidiary) {
            if (!subsidiary && currentCarSubsidiary) {
              setValue("subsidiary", currentCarSubsidiary);
            }
            return;
          }

          if (subsidiary.id !== currentCarSubsidiary.id) {
            setShowSubsidiaryMismatchDialog(true);
          }
        }, [currentCarSubsidiary?.id, isNewClaim]);

        const damageValueGross = watch("damageValueGross");
        const damageValueNet = watch("damageValueNet");
        const damageValueCurrency = watch("damageValueCurrency");
        const settledAmountGross = watch("settledAmountGross");
        const settledAmountNet = watch("settledAmountNet");
        const settledAmountCurrency = watch("settledAmountCurrency");

        return (
          <>
            <FormGrid columns={2}>
              {/* Number */}
              <FormField label={i18n.t.common.number} error={errors.number} isOptional>
                {({ labelId, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={isDisabled || isLoading}
                    {...register("number", { minLength: 1 })}
                  />
                )}
              </FormField>

              {/* Incident date */}
              <Controller
                name="incidentDate"
                control={control}
                rules={{
                  required: true,
                  validate: (value) => {
                    if (!value) return i18n.t.common.invalidDate;
                    const d = new Date(value);
                    if (d.getFullYear() < 1000) return i18n.t.common.invalidDate;
                    return undefined;
                  },
                }}
                render={({ fieldState }) => (
                  <FormField label={i18n.t.claims.incidentDate} error={fieldState.error}>
                    {({ isInvalid }) => (
                      <DatePicker
                        onChange={(v) => setValue("incidentDate", v!)}
                        isInvalid={isInvalid}
                        value={watch("incidentDate")}
                        disabled={isDisabled || isLoading}
                      />
                    )}
                  </FormField>
                )}
              />

              {/* Type */}
              <Controller
                name="type"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.type} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Select<ClaimTypeSearch>
                        menuPosition="fixed"
                        {...field}
                        inputId={labelId}
                        options={claimTypes.data?.items}
                        isLoading={isLoading || claimTypes.isLoading}
                        isDisabled={isDisabled || isLoading}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.name}
                        getOptionValue={(x) => x.id}
                        styles={reactSelectStyles<ClaimTypeSearch>(isInvalid)}
                      />
                    )}
                  </FormField>
                )}
              />

              {/* Car */}
              <Controller
                name="car"
                control={control}
                rules={{ required: !carId }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.cars.singular} error={fieldState.error} hidden={!!carId}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<CarSearch, CarField>(aborter, searchTerm, carsApi.getCars, {
                        fields: CAR_FIELDS,
                      });

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultCarOptions);
                      }, []);

                      return (
                        <AsyncSelect
                          {...field}
                          inputId={labelId}
                          isDisabled={isDisabled || isLoading}
                          isLoading={isLoading}
                          defaultOptions={defaultCarOptions}
                          loadOptions={loadOptions}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) =>
                            x.currentPlateNumber || x.lastPlateNumber || x.vehicleIdentificationNumber
                          }
                          getOptionValue={(x) => x.id}
                          styles={reactSelectStyles<{
                            id: string;
                            vehicleIdentificationNumber: string;
                            currentPlateNumber?: string;
                            lastPlateNumber?: string;
                          }>(isInvalid)}
                          onChange={(v) => {
                            if (isNewClaim && v?.id) {
                              currentDriverUser.trigger(v.id);
                            }
                            field.onChange(v);
                          }}
                          menuPosition="fixed"
                        />
                      );
                    }}
                  </FormField>
                )}
              />

              {/* Driver */}
              <Controller
                name="driver"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.carDrivers.singular} error={fieldState.error}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<UserSearch, UserField>(
                        aborter,
                        searchTerm,
                        usersApi.getAdminUsers,
                        {
                          fields: USER_FIELDS,
                        },
                      );

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultUserOptions);
                      }, []);

                      if (carId) {
                        // scoped to a known car: offer only its historic drivers, no need for async search
                        return (
                          <Select<UserSearch>
                            {...field}
                            inputId={labelId}
                            isDisabled={isDisabled || isLoading}
                            isLoading={isLoading || carDrivers.isLoading}
                            options={carDrivers.data?.items}
                            placeholder={`${i18n.t.common.select}...`}
                            getOptionLabel={(x) => x.fullName}
                            getOptionValue={(x) => x.id}
                            styles={reactSelectStyles<UserSearch>(isInvalid)}
                            menuPosition="fixed"
                          />
                        );
                      }

                      return (
                        <AsyncSelect
                          {...field}
                          inputId={labelId}
                          isDisabled={isDisabled || isLoading}
                          isLoading={isLoading}
                          defaultOptions={defaultUserOptions}
                          loadOptions={loadOptions}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) => x.fullName}
                          getOptionValue={(x) => x.id}
                          styles={reactSelectStyles<UserSearch>(isInvalid)}
                          menuPosition="fixed"
                        />
                      );
                    }}
                  </FormField>
                )}
              />

              {/* Subsidiary */}
              <Controller
                name="subsidiary"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.subsidiaries.singular} error={fieldState.error}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<SubsidiarySearch, SubsidiaryField>(
                        aborter,
                        searchTerm,
                        subsidiariesApi.getAdminSubsidiaries,
                        {
                          fields: SUBSIDIARY_FIELDS,
                        },
                      );

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultSubsidiaryOptions);
                      }, []);

                      return (
                        <AsyncSelect
                          {...field}
                          inputId={labelId}
                          isDisabled={isDisabled || isLoading}
                          isLoading={isLoading}
                          defaultOptions={defaultSubsidiaryOptions}
                          loadOptions={loadOptions}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) => x.name}
                          getOptionValue={(x) => x.id}
                          styles={reactSelectStyles<SubsidiarySearch>(isInvalid)}
                          menuPosition="fixed"
                          onChange={(value) => {
                            field.onChange(value);

                            if (!isNewClaim || !value?.id || !currentCarSubsidiary?.id) {
                              return;
                            }

                            if (value.id !== currentCarSubsidiary.id) {
                              setShowSubsidiaryMismatchDialog(true);
                            } else {
                              setShowSubsidiaryMismatchDialog(false);
                            }
                          }}
                        />
                      );
                    }}
                  </FormField>
                )}
              />

              {/* Status */}
              <Controller
                name="status"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.state} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Select<ClaimStatusType>
                        menuPosition="fixed"
                        {...field}
                        inputId={labelId}
                        options={statuses}
                        isLoading={isLoading}
                        isDisabled={isDisabled || isLoading}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.label}
                        getOptionValue={(x) => x.value}
                        styles={reactSelectStyles<ClaimStatusType>(isInvalid)}
                      />
                    )}
                  </FormField>
                )}
              />

              {/* Opening date */}
              <Controller
                name="openingDate"
                control={control}
                rules={{
                  required: true,
                  validate: (value) => {
                    if (!value) return i18n.t.common.invalidDate;
                    const d = new Date(value);
                    if (d.getFullYear() < 1000) return i18n.t.common.invalidDate;
                    return undefined;
                  },
                }}
                render={({ fieldState }) => (
                  <FormField label={i18n.t.claims.openingDate} error={fieldState.error}>
                    {({ isInvalid }) => (
                      <DatePicker
                        onChange={(v) => setValue("openingDate", v!)}
                        isInvalid={isInvalid}
                        value={watch("openingDate")}
                        disabled={isDisabled || isLoading}
                      />
                    )}
                  </FormField>
                )}
              />

              {/* Closing date — only relevant when closed/rejected */}
              <Controller
                name="closingDate"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value) return undefined;
                    const d = new Date(value);
                    if (d.getFullYear() < 1000) return i18n.t.common.invalidDate;
                    return undefined;
                  },
                }}
                render={({ fieldState }) => (
                  <FormField label={i18n.t.claims.closingDate} error={fieldState.error} isOptional>
                    {({ isInvalid }) => (
                      <DatePicker
                        onChange={(v) => setValue("closingDate", v ?? undefined)}
                        isInvalid={isInvalid}
                        value={watch("closingDate")}
                        disabled={isDisabled || isLoading}
                      />
                    )}
                  </FormField>
                )}
              />

              {/* Damage value */}
              <FormField label={i18n.t.claims.damageValue} isOptional>
                {({ labelId }) => {
                  const isDamageValueNetRequired = !!damageValueCurrency && !damageValueGross;
                  const isDamageValueGrossRequired = !!damageValueCurrency && !damageValueNet;
                  const isDamageValueCurrencyRequired = !!damageValueNet || !!damageValueGross;

                  register("damageValueNet", { required: isDamageValueNetRequired });
                  register("damageValueGross", { required: isDamageValueGrossRequired });
                  register("damageValueCurrency", { required: isDamageValueCurrencyRequired });

                  return (
                    <CostInput
                      isDisabled={isDisabled}
                      isLoading={isLoading}
                      id={labelId}
                      onSetCostNet={(v) => setValue("damageValueNet", v)}
                      onSetCostGross={(v) => setValue("damageValueGross", v)}
                      onSetCurrency={(v) => setValue("damageValueCurrency", v)}
                      costNet={damageValueNet ?? null}
                      costGross={damageValueGross ?? null}
                      currency={damageValueCurrency ?? null}
                      isNetInvalid={!!errors.damageValueNet}
                      isGrossInvalid={!!errors.damageValueGross}
                      isCurInvalid={!!errors.damageValueCurrency}
                    />
                  );
                }}
              </FormField>

              {/* Settled amount */}
              <FormField label={i18n.t.claims.settledAmount} isOptional>
                {({ labelId }) => {
                  const isSettledAmountNetRequired = !!settledAmountCurrency && !settledAmountGross;
                  const isSettledAmountGrossRequired = !!settledAmountCurrency && !settledAmountNet;
                  const isSettledAmountCurrencyRequired = !!settledAmountNet || !!settledAmountGross;

                  register("settledAmountNet", { required: isSettledAmountNetRequired });
                  register("settledAmountGross", { required: isSettledAmountGrossRequired });
                  register("settledAmountCurrency", { required: isSettledAmountCurrencyRequired });

                  return (
                    <CostInput
                      isDisabled={isDisabled}
                      isLoading={isLoading}
                      id={labelId}
                      onSetCostNet={(v) => setValue("settledAmountNet", v)}
                      onSetCostGross={(v) => setValue("settledAmountGross", v)}
                      onSetCurrency={(v) => setValue("settledAmountCurrency", v)}
                      costNet={settledAmountNet ?? null}
                      costGross={settledAmountGross ?? null}
                      currency={settledAmountCurrency ?? null}
                      isNetInvalid={!!errors.settledAmountNet}
                      isGrossInvalid={!!errors.settledAmountGross}
                      isCurInvalid={!!errors.settledAmountCurrency}
                    />
                  );
                }}
              </FormField>

              {/* Description */}
              <FormField label={i18n.t.common.description} error={errors.description} isOptional fullWidth>
                {({ labelId, isInvalid }) => (
                  <Textarea
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={isDisabled || isLoading}
                    {...register("description", { minLength: 1 })}
                  />
                )}
              </FormField>
            </FormGrid>

            {!showSubsidiaryMismatchDialog && showDriverMismatchDilog && (
              <ModalDialog
                onClose={() => setShowDriverMismatchDilog(false)}
                isSubmitting={false}
                actions={[
                  {
                    title: i18n.t.common.no,
                    onClick: () => setShowDriverMismatchDilog(false),
                  } satisfies ActionButton,
                  {
                    title: i18n.t.common.yes,
                    onClick: () => {
                      setValue("driver", currentDriverUser.data);
                      setShowDriverMismatchDilog(false);
                    },
                    type: "secondary",
                  } satisfies ActionButton,
                ]}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: i18n.t.claims.questions.switchDriver(
                      watch("driver")?.fullName ?? "",
                      currentDriverUser.data?.fullName ?? "",
                    ),
                  }}
                ></div>
              </ModalDialog>
            )}

            {showSubsidiaryMismatchDialog && (
              <ModalDialog
                onClose={() => setShowSubsidiaryMismatchDialog(false)}
                isSubmitting={false}
                actions={[
                  {
                    title: i18n.t.common.no,
                    onClick: () => setShowSubsidiaryMismatchDialog(false),
                  } satisfies ActionButton,
                  {
                    title: i18n.t.common.yes,
                    onClick: () => {
                      currentCarSubsidiary && setValue("subsidiary", currentCarSubsidiary);
                      setShowSubsidiaryMismatchDialog(false);
                    },
                    type: "secondary",
                  } satisfies ActionButton,
                ]}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: i18n.t.claims.questions.switchSubsidiary(
                      watch("subsidiary")?.name ?? "",
                      currentCarSubsidiary?.name ?? "",
                    ),
                  }}
                ></div>
              </ModalDialog>
            )}
          </>
        );
      }}
    </Form>
  );
};

export default ClaimForm;
